import { useApp } from '../contexts/App';

/**
 * Hook to check if the current user has specific permissions
 */
export const usePermission = () => {
    const { user } = useApp();

    /**
     * Check if user has a specific permission
     */
    const hasPermission = (permission: string): boolean => {
        // Super admins (without role) have all permissions
        if (!user?.role_id && user?.groups?.includes('Administrators')) {
            return true;
        }

        // Check if user has the specific permission
        const userPermissions: string[] = user?.permissions || [];
        return userPermissions.includes(permission);
    };

    /**
     * Check if user has any of the given permissions
     */
    const hasAnyPermission = (permissions: string[]): boolean => {
        // Super admins have all permissions
        if (!user?.role_id && user?.groups?.includes('Administrators')) {
            return true;
        }

        const userPermissions: string[] = user?.permissions || [];
        return permissions.some((permission) => userPermissions.includes(permission));
    };

    /**
     * Check if user has all of the given permissions
     */
    const hasAllPermissions = (permissions: string[]): boolean => {
        // Super admins have all permissions
        if (!user?.role_id && user?.groups?.includes('Administrators')) {
            return true;
        }

        const userPermissions: string[] = user?.permissions || [];
        return permissions.every((permission) => userPermissions.includes(permission));
    };

    /**
     * Check if user is a super admin (no role restriction)
     */
    const isSuperAdmin = (): boolean => {
        return !user?.role_id && user?.groups?.includes('Administrators');
    };

    return {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isSuperAdmin,
        permissions: user?.permissions || [],
    };
};

/**
 * Permission mapping for routes and menu items
 * Maps path patterns to required permissions
 */
export const ROUTE_PERMISSIONS: { [key: string]: string[] } = {
    // Trail Management
    '/trail/trails': ['trails.view'],
    '/trail/trails/create': ['trails.create'],
    '/trail/trails/update': ['trails.edit'],
    '/trail/trails/history': ['trails.view'],

    // Marathon Trail
    '/trail/marathon-trail': ['marathon_trails.view'],
    '/trail/marathon-trail/create': ['marathon_trails.create'],
    '/trail/marathon-trail/update': ['marathon_trails.edit'],

    // Stay
    '/trail/stay': ['stay.view'],
    '/trail/stay/create': ['stay.create'],
    '/trail/stay/update': ['stay.edit'],

    // Eat Local
    '/trail/eat-local': ['eat_local.view'],
    '/trail/eat-local/create': ['eat_local.create'],
    '/trail/eat-local/update': ['eat_local.edit'],

    // Challenges
    '/trail/challenges': ['challenges.view'],
    '/trail/challenges/details': ['challenges.view'],

    // Reviews
    '/trail/reviews': ['reviews.view'],
    '/trail/reviews/create': ['reviews.create'],
    '/trail/reviews/update': ['reviews.edit'],

    // Photos
    '/trail/photos': ['photos.view'],
    '/trail/photos/create': ['photos.create'],
    '/trail/photos/update': ['photos.edit'],

    // Activities
    '/activities': ['activities.view'],
    '/activities/create': ['activities.create'],
    '/activities/update': ['activities.edit'],

    // Ranking
    '/ranking': ['ranking.view'],

    // Membership
    '/membership': ['membership.view'],
    '/membership/create': ['membership.create'],
    '/membership/update': ['membership.edit'],

    // ExoSeed
    '/exoseed': ['exoseed.view'],
    '/exoseed/campaigns': ['exoseed.view'],
    '/exoseed/campaigns/create': ['exoseed.create'],
    '/exoseed/campaigns/update': ['exoseed.edit'],

    // Events
    '/events': ['events.view'],
    '/events/create': ['events.create'],
    '/events/update': ['events.edit'],

    // Ticketing
    '/tickets': ['tickets.view'],
    '/my-tickets': ['tickets.my_tickets'],
    '/tickets/update': ['tickets.edit'],

    // Crawl
    '/crawl': ['crawl.view'],
    '/crawl/update': ['crawl.edit'],

    // User Management
    '/user/customers': ['users.view'],
    '/user/customers/create': ['users.create'],
    '/user/customers/update': ['users.edit'],

    // Admin Management
    '/user/admins': ['admins.view'],
    '/user/admins/create': ['admins.create'],
    '/user/admins/update': ['admins.edit'],

    // Role Management
    '/user/roles': ['roles.view'],
    '/user/roles/create': ['roles.create'],
    '/user/roles/update': ['roles.edit'],

    // Settings
    '/setting/ads': ['settings.ads'],
    '/setting/app-version': ['settings.app_version'],
    '/setting/app-setting': ['settings.app_setting'],
    '/trail/attributes': ['settings.attributes'],
    '/setting/app-notification': ['settings.notification'],
    '/setting/gps': ['settings.gps'],
};

/**
 * Get required permissions for a given path
 */
export const getRequiredPermissions = (path: string): string[] => {
    // Direct match
    if (ROUTE_PERMISSIONS[path]) {
        return ROUTE_PERMISSIONS[path];
    }

    // Pattern match (for paths with params like /update/:id)
    for (const routePath of Object.keys(ROUTE_PERMISSIONS)) {
        if (path.startsWith(routePath)) {
            return ROUTE_PERMISSIONS[routePath];
        }
    }

    return [];
};

/**
 * Check if user has permission for a specific path
 */
export const checkPathPermission = (path: string, userPermissions: string[], isSuperAdmin: boolean): boolean => {
    if (isSuperAdmin) return true;

    const requiredPermissions = getRequiredPermissions(path);
    if (requiredPermissions.length === 0) return true; // No permissions required

    return requiredPermissions.some((permission) => userPermissions.includes(permission));
};
